import { supabase } from "@/integrations/supabase/client";

export interface CityStats {
  city: string;
  companiesCount: number;
  servicesCount: number;
}

export interface PopularService {
  serviceName: string;
  city: string;
  companiesCount: number;
  url: string;
}

// Città prioritarie (dalle PRIORITY_CITIES)
const PRIORITY_CITIES = [
  { slug: "jesolo", name: "Jesolo" },
  { slug: "cavallino", name: "Cavallino" },
  { slug: "caorle", name: "Caorle" },
  { slug: "venezia", name: "Venezia" },
  { slug: "bibione", name: "Bibione" },
  { slug: "eraclea", name: "Eraclea" },
];

// Servizi popolari per Local SEO
const POPULAR_SERVICES = [
  { name: "pulizie-appartamenti-vacanze", display: "Pulizie appartamenti vacanze", searchQuery: "pulizie appartamenti vacanze" },
  { name: "sanificazione-attivita-commerciali", display: "Sanificazione attività commerciali", searchQuery: "sanificazione" },
  { name: "pulizie-post-cantiere", display: "Pulizie post-cantiere", searchQuery: "pulizie post cantiere" },
  { name: "pulizie-uffici-aziende", display: "Pulizie uffici", searchQuery: "pulizie uffici" },
  { name: "pulizie-domestiche", display: "Pulizie domestiche", searchQuery: "pulizie domestiche" },
];

/**
 * Recupera statistiche per città (numero aziende)
 */
export async function getCityStats(): Promise<CityStats[]> {
  try {
    const { data: companies, error } = await supabase
      .from("companies")
      .select("city, id, services!inner(id)")
      .eq("is_active", true)
      .eq("is_deleted", false)
      .eq("onboarding_completed", true);

    if (error) {
      console.warn("Error fetching city stats:", error);
      return PRIORITY_CITIES.map(c => ({ city: c.name, companiesCount: 0, servicesCount: 0 }));
    }

    // Aggrega per città
    const cityMap = new Map<string, { companies: Set<string>, services: Set<string> }>();
    
    (companies || []).forEach((c: any) => {
      if (!c.city) return;
      const city = c.city.trim();
      if (!cityMap.has(city)) {
        cityMap.set(city, { companies: new Set(), services: new Set() });
      }
      const entry = cityMap.get(city)!;
      entry.companies.add(c.id);
      if (c.services && Array.isArray(c.services)) {
        c.services.forEach((s: any) => entry.services.add(s.id));
      }
    });

    const stats: CityStats[] = Array.from(cityMap.entries())
      .map(([city, data]) => ({
        city,
        companiesCount: data.companies.size,
        servicesCount: data.services.size,
      }))
      .filter(s => s.companiesCount > 0)
      .sort((a, b) => b.companiesCount - a.companiesCount);

    // Se non ci sono dati reali, usa placeholder per città prioritarie
    if (stats.length === 0) {
      return PRIORITY_CITIES.map(c => ({ 
        city: c.name, 
        companiesCount: 5, // placeholder
        servicesCount: 10 
      }));
    }

    return stats;
  } catch (error) {
    console.warn("Error in getCityStats:", error);
    return PRIORITY_CITIES.map(c => ({ 
      city: c.name, 
      companiesCount: 5, 
      servicesCount: 10 
    }));
  }
}

/**
 * Genera lista servizi popolari per città (per Local SEO)
 * I link portano alla ricerca di aziende per il cliente, non alle landing per aziende
 */
export function getPopularServicesByCity(): PopularService[] {
  const services: PopularService[] = [];
  
  PRIORITY_CITIES.forEach(city => {
    POPULAR_SERVICES.forEach(service => {
      services.push({
        serviceName: service.display,
        city: city.name,
        companiesCount: 3, // placeholder - in futuro si può calcolare dal DB
        url: `/?q=${encodeURIComponent(service.searchQuery)}&city=${encodeURIComponent(city.name)}`,
      });
    });
  });

  return services;
}

/**
 * Verifica se un'azienda opera in una città specifica
 */
export function companyOperatesInCity(company: any, city: string): boolean {
  if (!city) return false;
  const cityLower = city.toLowerCase().trim();
  
  // Check company city
  if (company.city && company.city.toLowerCase().includes(cityLower)) {
    return true;
  }
  
  // Check service_areas
  if (company.service_areas && Array.isArray(company.service_areas)) {
    return company.service_areas.some((area: string) => 
      area.toLowerCase().includes(cityLower)
    );
  }
  
  return false;
}
